import React, {useMemo, forwardRef, useImperativeHandle, useEffect} from 'react';
import { Typography } from '@curves/ui-base';
import clsx from 'clsx';
import {formatCurrencyFn, isFunction} from '@Util/utils';
import {computeMaxRefundForRow} from '@Local/Orders/Componetnets/SalesAllowanceItemTable/SalesAllowanceItemTable.util';
import OverviewListTable from '@Local/Components/OverviewList/OverviewListTable';
import {ProductCard} from '@Local/Components/InfoCard';
/**
 * SalesAllowanceReadonlyTable
 *
 * 唯讀版本的折讓項目表格。
 *
 * 功能：
 * - 顯示訂單商品的基本資訊（品項、類型、單價、數量、已折讓狀態、可退金額、小計、備註）
 * - 自動計算「本次可退金額」與「折讓小計」
 * - 在初始化與資料變動時，將總計結果回傳給父層
 * - 永遠標記為「沒有輸入錯誤」（唯讀狀態）
 * - 提供父層透過 ref 操作：`getResult()`、`isError()`、`getTotal()`
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.className] - 外層容器的自定義 class
 * @param {Array<Object>} [props.items=[]] - 要顯示的商品資料列
 *   @param {number} props.items[].orderItemID - 訂單項目 ID
 *   @param {number} props.items[].orderID - 訂單 ID
 *   @param {string} props.items[].productName - 商品名稱
 *   @param {string} props.items[].productTypeText - 商品類型描述
 *   @param {number} props.items[].salePrice - 單價
 *   @param {number} props.items[].quantity - 數量
 *   @param {number} [props.items[].returnPrice] - 已折讓金額（若有）
 *   @param {number} [props.items[].returnQuantity] - 已折讓數量（若有）
 *   @param {boolean} [props.items[].onlyAllowancePrice=false] - 是否為「僅金額可調整」類型
 *   @param {string} [props.items[].notes] - 備註文字
 * @param {Object} [props.overviewProps={}] - 傳入給 OverviewListTable 的額外設定（分頁、樣式等）
 * @param {Function} [props.onTotalChange] - 當總計變更時呼叫 (total: number) => void
 * @param {Function} [props.onValidityChange] - 當有效性狀態變更時呼叫 (hasError: boolean) => void（唯讀情境固定為 false）
 *
 * @returns {JSX.Element} 唯讀折讓表格
 *
 * @example
 * <SalesAllowanceReadonlyTable
 *   items={[
 *     {
 *       orderItemID: 1,
 *       orderID: 101,
 *       productName: "測試商品",
 *       productTypeText: "一般商品",
 *       salePrice: 100,
 *       quantity: 2,
 *       returnPrice: 50,
 *       returnQuantity: 1,
 *       notes: "已退過一次"
 *     }
 *   ]}
 *   onTotalChange={(total) => console.log("總計：", total)}
 * />
 *
 * // ref 操作範例
 * const ref = useRef();
 * const total = ref.current.getTotal(); // 總計
 * const result = ref.current.getResult(); // 每筆折讓資料
 */
const SalesAllowanceReadonlyTable = forwardRef(({
                                                    className,
                                                    items = [],
                                                    overviewProps = {},
                                                    onTotalChange,
                                                    onValidityChange
                                                }, ref) => {
    const headerRow = useMemo(() => {
        return [
            {
                cellKey: "product",
                headerLabel: "品項",
                minWidth: 200,
                className: "vertical-align-middle",
                formatCell: (_v, row) => <ProductCard productName={row.productName} pictureSrc={row.pictureSrc}/>,
            },
            {
                cellKey: "type",
                headerLabel: "類型",
                align: "center",
                minWidth: 160,
                className: "vertical-align-middle",
                formatCell: (_v, row) => (
                        <Typography className="mb-0">{row.productTypeText}</Typography>
                ),
            },
            {
                cellKey: "unitPrice",
                headerLabel: "單價",
                align: "right",
                minWidth: 120,
                className: "vertical-align-middle",
                formatCell: (_v, row) => formatCurrencyFn(row.salePrice),
            },
            {
                cellKey: "quantity",
                headerLabel: "數量",
                align: "center",
                minWidth: 100,
                className: "vertical-align-middle",
                formatCell: (_v, row) => (row.onlyAllowancePrice ? "" : row.quantity),
            },
            {
                cellKey: "returned",
                headerLabel: "已折讓數量/金額",
                align: "center",
                minWidth: 160,
                className: "vertical-align-middle",
                formatCell: (_v, row) =>
                    row.onlyAllowancePrice
                        ? formatCurrencyFn(row.returnPrice || 0)
                        : (row.returnQuantity ?? 0),
            },
            {
                cellKey: "maxRefund",
                headerLabel: "本次可退",
                align: "center",
                minWidth: 160,
                className: "vertical-align-middle",
                formatCell: (_v, row) => {
                    const r = computeMaxRefundForRow(row);
                    return row.onlyAllowancePrice ? formatCurrencyFn(r.allowancePrice) : r.allowanceQuantity;
                },
            },
            {
                cellKey: "subtotal",
                headerLabel: <strong>折讓小計</strong>,
                align: "right",
                minWidth: 160,
                className: "vertical-align-middle",
                formatCell: (_v, row) => {
                    const r = computeMaxRefundForRow(row);
                    return <strong>{formatCurrencyFn(r.subtotal)}</strong>;
                },
            },
            {
                cellKey: "notes",
                headerLabel: "備註",
                minWidth: 160,
                className: "vertical-align-middle",
                formatCell: (_v, row) => row.notes || "",
            },
        ];
    }, []);
    const total = useMemo(() => items.reduce((acc, row) => acc + computeMaxRefundForRow(row).subtotal, 0),
        // eslint-disable-next-line
        [items]);
    useEffect(() => {
            isFunction(onTotalChange) && onTotalChange(total);
            // 唯讀沒有輸入錯誤
            isFunction(onValidityChange) && onValidityChange(false);
        },
        // eslint-disable-next-line
        [total, onTotalChange, onValidityChange]);
    // 提供 for 容器的 ref：getResult / getTotal（便於沿用相同容器流程）
    useImperativeHandle(ref, () => ({
        getResult: () => {
            return items.map((row) => {
                const r = computeMaxRefundForRow(row);
                return {
                    orderItemID: row.orderItemID,
                    orderID: row.orderID,
                    allowancePrice: r.allowancePrice,
                    allowanceQuantity: r.allowanceQuantity,
                };
            });
        },
        isError: () => false,
        getTotal: () => items.reduce((acc, row) => acc + computeMaxRefundForRow(row).subtotal, 0),
    }), [items]);
    return (
        <OverviewListTable
            className={clsx("sales-allowance-readonly-table", className)}
            headerRow={headerRow}
            list={items}
            isPagination={overviewProps.isPagination || overviewProps.isMasonryPagination || false}
            isMasonryPagination={overviewProps.isMasonryPagination || false}
            onChangePage={overviewProps.onChangePage}
            pageIndex={overviewProps.pageIndex}
            totalPage={overviewProps.totalPage}
            isGray={overviewProps.isGray || true}
            tableRowHeaderProps={overviewProps.tableRowHeaderProps}
            tableRowBodyProps={overviewProps.tableRowBodyProps}
        />
    );
})
export default SalesAllowanceReadonlyTable;
